import { getWorldCupTeamBrandingByCode } from '@shared/worldCupTeamBranding';

type RgbColor = readonly [number, number, number];

const parseHexColor = (color: string): RgbColor | null => {
  const value = color.trim().replace(/^#/, '');
  const expanded = value.length === 3
    ? [...value].map((channel) => `${channel}${channel}`).join('')
    : value;

  if (!/^[\da-f]{6}$/i.test(expanded)) return null;

  return [
    Number.parseInt(expanded.slice(0, 2), 16),
    Number.parseInt(expanded.slice(2, 4), 16),
    Number.parseInt(expanded.slice(4, 6), 16),
  ];
};

const getRelativeLuminance = ([red, green, blue]: RgbColor): number => {
  const linearize = (channel: number) => {
    const normalized = channel / 255;
    return normalized <= 0.04045
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  };

  return (0.2126 * linearize(red)) + (0.7152 * linearize(green)) + (0.0722 * linearize(blue));
};

const getContrastRatio = (first: number, second: number) => (
  (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
);

/** Choose whichever supplied foreground has stronger WCAG contrast against a team color. */
export function getContrastingTextColor(
  backgroundColor: string,
  lightForeground: string,
  darkForeground: string,
): string {
  const background = parseHexColor(backgroundColor);
  const light = parseHexColor(lightForeground);
  const dark = parseHexColor(darkForeground);
  if (!background || !light || !dark) return darkForeground;

  const backgroundLuminance = getRelativeLuminance(background);
  const lightContrast = getContrastRatio(backgroundLuminance, getRelativeLuminance(light));
  const darkContrast = getContrastRatio(backgroundLuminance, getRelativeLuminance(dark));
  return lightContrast > darkContrast ? lightForeground : darkForeground;
}

const TEAM_PRIMARY_COLORS: Record<string, string> = {
  // AFC East
  BUF: '#00338D',
  MIA: '#008E97',
  NE: '#002244',
  NYJ: '#125740',

  // AFC North
  BAL: '#241773',
  CIN: '#000000',
  CLE: '#311D00',
  PIT: '#FFB612',

  // AFC South
  HOU: '#03202F',
  IND: '#002C5F',
  JAX: '#101820',
  TEN: '#0C2340',

  // AFC West
  DEN: '#FB4F14',
  KC: '#E31837',
  LV: '#000000',
  LAC: '#0080C6',

  // NFC East
  DAL: '#003594',
  NYG: '#0B2265',
  PHI: '#004C54',
  WAS: '#5A1414',

  // NFC North
  CHI: '#0B162A',
  DET: '#0076B6',
  GB: '#203731',
  MIN: '#4F2683',

  // NFC South
  ATL: '#A71930',
  CAR: '#0085CA',
  NO: '#D3BC8D',
  TB: '#D50A0A',

  // NFC West
  ARI: '#97233F',
  LAR: '#003594',
  SF: '#AA0000',
  SEA: '#002244',
};

const TEAM_SECONDARY_COLORS: Record<string, string> = {
  // AFC East
  BUF: '#C60C30',
  MIA: '#FC4C02',
  NE: '#C60C30',
  NYJ: '#000000',

  // AFC North
  BAL: '#000000',
  CIN: '#FB4F14',
  CLE: '#FF3C00',
  PIT: '#101820',

  // AFC South
  HOU: '#A71930',
  IND: '#A2AAAD',
  JAX: '#D7A22A',
  TEN: '#4B92DB',

  // AFC West
  DEN: '#002244',
  KC: '#FFB81C',
  LV: '#A5ACAF',
  LAC: '#FFC20E',

  // NFC East
  DAL: '#041E42',
  NYG: '#A71930',
  PHI: '#A5ACAF',
  WAS: '#FFB612',

  // NFC North
  CHI: '#C83803',
  DET: '#B0B7BC',
  GB: '#FFB612',
  MIN: '#FFC62F',

  // NFC South
  ATL: '#000000',
  CAR: '#101820',
  NO: '#101820',
  TB: '#FF7900',

  // NFC West
  ARI: '#000000',
  LAR: '#FFA300',
  SF: '#B3995D',
  SEA: '#69BE28',
};

function parseHexChannel(hex: string): number {
  return parseInt(hex, 16);
}

function applyAlpha(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return hex;
  const red = parseHexChannel(normalized.slice(0, 2));
  const green = parseHexChannel(normalized.slice(2, 4));
  const blue = parseHexChannel(normalized.slice(4, 6));
  return `rgba(${red}, ${green}, ${blue}, ${Math.min(Math.max(alpha, 0), 1)})`;
}

export function getTeamPrimaryColor(abbreviation: string | null | undefined): string | null {
  if (!abbreviation) return null;
  const normalized = abbreviation.toUpperCase();
  return TEAM_PRIMARY_COLORS[normalized] ?? getWorldCupTeamBrandingByCode(normalized)?.primaryColor ?? null;
}

export function getTeamSecondaryColor(abbreviation: string | null | undefined): string | null {
  if (!abbreviation) return null;
  const normalized = abbreviation.toUpperCase();
  return TEAM_SECONDARY_COLORS[normalized] ?? getWorldCupTeamBrandingByCode(normalized)?.secondaryColor ?? null;
}

export function getTeamPrimaryColorWithAlpha(
  abbreviation: string | null | undefined,
  alpha: number,
): string | null {
  const color = getTeamPrimaryColor(abbreviation);
  return color ? applyAlpha(color, alpha) : null;
}
